import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import debounce from 'lodash/debounce';
import { dataLayerPush } from '@ertelecom/ui-react/components/helpers';
import { IMenuItem } from '~/src/api/content/menu';
import MenuLogo from '~/src/components/MenuLogo';
import CallRequestByTheme from '~/src/components/Popups/CallRequestByTheme';
import TopMenu from '~/src/components/Header/TopMenu';
import RightMenu from '~/src/components/Header/RightMenu';
import MainMenu from '~/src/components/Header/MainMenu';
import Profile, { LK_HREF } from '~/src/components/Header/Profile';
import { ICurrentCity } from '~/src/store/reducers/city';
import { IClientPersonal } from '~/src/store/reducers/auth';
import { useMatchMedia } from '~/src/helpers/hooks/useMatchMedia';
import { EVENTS } from '~/src/constants/analytics';
import { Profile as Login } from './Profile/styled';
import * as ST from './styled';

enum EMediaWidth {
    tablet = '(max-width: 1365px)',
    mobile = '(max-width: 767px)',
}

interface IProps {
    items: Record<string, IMenuItem[]>;
    openCitySelect: () => void;
    city: ICurrentCity;
    billingDomain?: string;
    cookieDomain?: string;
    isAuth: boolean;
    logout: () => void;
    clientInfo: IClientPersonal;
    productsInfo: {
        productId: number;
    }[];
    plankIsShown: boolean;
    enableBecomeClientPopup: boolean;
    sendBecomeClient: (data: { name: string, phone: string }) => void;
}

export const PAYMENTS_ITEM_ID = 293;
export const PROMO_ITEM_ID = 303;
export const SERVICE_ITEM_ID = 313;

const SCROLL_TOP = 26;

const VIDEOCONTROL_ITEM_WEIGHT = 4;
const DOMOFON_ITEM_WEIGHT = 5;

const burgerOpen = '/static/images/menu/burger.svg';
const burgerClose = '/static/images/menu/cross.svg';

const Header = ({
                     items,
                     openCitySelect,
                     city,
                     billingDomain,
                     cookieDomain,
                     isAuth,
                     logout,
                     clientInfo,
                     productsInfo,
                     plankIsShown,
                     enableBecomeClientPopup,
                 }: IProps): JSX.Element => {
    const { TopLeft = [], TopMain = [], TopRight = [] } = items || {};

    const href = useMemo(() => `tel:+${city.phone.replace(/\D/g, '')}`, [city]);

    const [isFixed, setIsFixed] = useState(false);

    useEffect(() => {
        const onScroll = () => {
            if (window.innerWidth > 1365) {
                setIsFixed(window.scrollY >= SCROLL_TOP);
            }
        };
        window.addEventListener('scroll', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const TopMainItems = useMemo(() => {
        return [...TopMain.filter(item => item.weight !== VIDEOCONTROL_ITEM_WEIGHT && item.weight !== DOMOFON_ITEM_WEIGHT)];
    }, [TopMain]);

    const MobileMainMenuItems = useMemo(() => {
        const SortedItems = [...TopMain, ...TopRight].sort((a, b) => a.weight - b.weight);
        return SortedItems.reduce((acc: IMenuItem[], item) => {
            if (item.id === PROMO_ITEM_ID || item.id === PAYMENTS_ITEM_ID) {
                acc.splice(0, 0, item);
            } else if (item.id === SERVICE_ITEM_ID) {
                acc.splice(SortedItems.length - 1, 0, item);
            } else {
                acc.push(item);
            }

            return acc;
        }, []);
    }, [TopMainItems, TopRight]);

    const TabletMainMenuItems = useMemo(() => {
        return MobileMainMenuItems.reduce((acc: Record<string, IMenuItem[]>, item, index) => {
            if (item.id === PROMO_ITEM_ID) {
                acc.reducedLeftSide.unshift(item);
            } else if (item.id === PAYMENTS_ITEM_ID) {
                acc.reducedRightSide.unshift(item);
            } else if (item.id === SERVICE_ITEM_ID) {
                acc.reducedRightSide.push(item);
            } else if (index % 2 === 0) {
                acc.reducedLeftSide.push(item);
            } else {
                acc.reducedRightSide.push(item);
            }

            return acc;
        }, {
            reducedLeftSide: [],
            reducedRightSide: []
        });
    }, [MobileMainMenuItems]);

    const [isBurgerOpen, setIsBurgerOpen] = useState(false);
    const [burgerVisibility, setBurgerVisibility] = useState({ display: 'none' });
    const [isCallbackOpen, setIsCallbackOpen] = useState(false);

    const [isTablet] = useMatchMedia(EMediaWidth.tablet);
    const [isMobile] = useMatchMedia(EMediaWidth.mobile);

    const isTabletBreakpoint = isTablet && !isMobile;

    const sendAuthEvent = useCallback(() => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: 'Auth',
            action: 'click authorization in new header',
        });
    }, []);

    const menuItemClick = useCallback((item, menuType = 'Main menu') => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: `${menuType} click`,
            action: item,
            label: 'menu_new',
        });
    }, []);

    const menuItemHover = useCallback(
        debounce((item, menuType = 'Main menu') => {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: `${menuType} hover`,
                action: item,
                label: 'menu_new',
            });
        }, 500),
        []
    );

    const toggleBurgerHandler = useCallback(() => {
        setIsBurgerOpen(!isBurgerOpen);
        setBurgerVisibility({ display: isBurgerOpen ? 'none' : 'block' });
    }, [isBurgerOpen]);

    const closeBurgerHandler = useCallback(() => {
        if (isBurgerOpen) {
            setIsBurgerOpen(false);
            setBurgerVisibility({ display: 'none' });
        }
    }, [isBurgerOpen]);

    const openCallbackPopup = useCallback(() => {
        setIsCallbackOpen(true);
        setIsBurgerOpen(false);
        setBurgerVisibility({ display: 'none' });
    }, []);

    return (
        <ST.Wrapper isAuth={isAuth} isFixed={isFixed} withPlank={plankIsShown}>
            <ST.HeaderWrapper isAuth={isAuth}>
                <ST.TopWrapper isFixed={isFixed}>
                    <ST.DesktopBlock>
                        <TopMenu
                            menuItems={TopLeft}
                            isTablet={isTabletBreakpoint}
                            onClick={menuItemClick}
                            onHover={menuItemHover}
                        />
                        <RightMenu
                            menuItems={TopRight}
                            openCitySelect={openCitySelect}
                            city={city}
                            cookieDomain={cookieDomain}
                            isAuth={isAuth}
                            onClick={menuItemClick}
                            onHover={menuItemHover}
                        />
                    </ST.DesktopBlock>
                    <ST.TabletBlock>
                        <ST.BurgerCitySelect
                            onClick={openCitySelect}
                            data-test='city-select-btn'
                        >
                            {city ? city.name : null}
                        </ST.BurgerCitySelect>
                        {!isAuth &&
                        <ST.MobileBlock onClick={closeBurgerHandler}>
                            <Link href={LK_HREF} passHref>
                                <Login onClick={sendAuthEvent}>
                                    <span>Войти</span>
                                </Login>
                            </Link>
                        </ST.MobileBlock>
                        }
                    </ST.TabletBlock>
                </ST.TopWrapper>
                <ST.MainWrapper>
                    <ST.Logo>
                        <Link href='/' passHref>
                            <MenuLogo href='/' billingDomain={billingDomain} cityData={city}/>
                        </Link>
                    </ST.Logo>
                    <ST.DesktopBlock>
                        <MainMenu
                            menuItems={TopMain}
                            isTablet={isTabletBreakpoint}
                            isFixed={isFixed}
                            onClick={menuItemClick}
                            onHover={menuItemHover}
                        />
                    </ST.DesktopBlock>
                    <ST.TabletBlock>
                        <ST.BurgerIcon onClick={toggleBurgerHandler}>
                            <ST.Burger
                                src={isBurgerOpen ? burgerClose : burgerOpen}
                                alt='Меню'
                            />
                        </ST.BurgerIcon>
                    </ST.TabletBlock>
                    <ST.Right>
                        {(city && city.phone) &&
                        <ST.DesktopBlock>
                            <ST.HeaderPhone
                                href={href}
                                className='js-calltouch-phone'
                                data-test='phone'
                            >
                                {city.phone}
                            </ST.HeaderPhone>
                        </ST.DesktopBlock>
                        }
                        <Profile
                            isAuth={isAuth}
                            productsInfo={productsInfo}
                            clientInfo={clientInfo}
                            logout={logout}
                            isMobile={isMobile}
                            closeHandler={closeBurgerHandler}
                            enableBecomeClientPopup={enableBecomeClientPopup}
                            city={city}
                        />
                    </ST.Right>
                </ST.MainWrapper>
            </ST.HeaderWrapper>
            {isTablet &&
                <ST.BurgerMenu style={burgerVisibility} isAuth={isAuth} withPlank={plankIsShown}>
                    <ST.BurgerMenuWrapper isAuth={isAuth}>
                        <div>
                            <ST.TopMenuBlock>
                                <TopMenu
                                    menuItems={TopLeft}
                                    isTablet={isTabletBreakpoint}
                                    onClick={menuItemClick}
                                    onHover={menuItemHover}
                                />
                            </ST.TopMenuBlock>
                            {city && city.phone
                                ? <ST.HeaderPhone
                                        href={href}
                                        className='js-calltouch-phone'
                                        data-test='phone'
                                    >
                                        {city.phone}
                                    </ST.HeaderPhone>
                                : null
                            }
                            <ST.CallbackLink
                                onClick={openCallbackPopup}
                                as='div'
                                data-test='callback-btn'
                            >
                                Заказать звонок
                            </ST.CallbackLink>
                        </div>
                        <ST.MainMenuRow>
                            {!isMobile
                                ? <>
                                    <MainMenu
                                        menuItems={TabletMainMenuItems.reducedLeftSide}
                                        isTablet={isTablet}
                                        onClick={menuItemClick}
                                        onHover={menuItemHover}
                                        clickHandler={toggleBurgerHandler}
                                    />
                                    <MainMenu
                                        menuItems={TabletMainMenuItems.reducedRightSide}
                                        isTablet={isTablet}
                                        onClick={menuItemClick}
                                        onHover={menuItemHover}
                                        clickHandler={toggleBurgerHandler}
                                    />
                                </>
                                : <MainMenu
                                    menuItems={MobileMainMenuItems}
                                    isTablet={isTablet}
                                    onClick={menuItemClick}
                                    onHover={menuItemHover}
                                    clickHandler={toggleBurgerHandler}
                                />
                            }
                        </ST.MainMenuRow>
                        <CallRequestByTheme
                            isOpen={isCallbackOpen}
                            onClose={() => setIsCallbackOpen(false)}
                        />
                    </ST.BurgerMenuWrapper>
                </ST.BurgerMenu>
            }
        </ST.Wrapper>
    );
};

export default Header;
